import { create } from 'zustand'
import {
  isLoggedIn,
  getToken,
  getRefreshToken,
  getUserFromToken,
  refreshTokenIfNeeded,
  clearTokens,
  exchangeCodeForToken,
  redirectToLogin,
  redirectToSignUp,
  logout as authLogout,
  getAnonSession,
  setAnonSession,
  clearAnonSession,
} from '../lib/auth'
import { authApi, anonApi, teamApi } from '../lib/api'
import { getEffectivePlan } from '../lib/subscription'

function hasTrustedPlatformAdminState(payload = {}) {
  return Boolean(
    payload?.isPlatformAdmin === true ||
    payload?.billing?.isPlatformAdmin === true
  )
}

const useAuthStore = create((set, get) => ({
  // --- State ---
  user: null,
  org: null,
  usage: null,
  isAuthenticated: false,
  isLoading: true,
  anonSession: null,

  // --- Initialise on app load ---
  init: async () => {
    set({ isLoading: true })

    // Handle OAuth callback
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')

    if (code) {
      window.history.replaceState({}, '', '/')
      const ok = await exchangeCodeForToken(code)
      if (ok) {
        await get().loadUser()
        await get().acceptPendingInviteIfNeeded()
      } else {
        set({ isLoading: false, isAuthenticated: false })
      }
      return
    }

    // Check existing session
    if (isLoggedIn()) {
      await get().loadUser()
    } else if (getToken() && getRefreshToken()) {
      const restored = await refreshTokenIfNeeded()
      if (restored && isLoggedIn()) {
        await get().loadUser()
        return
      }

      clearTokens()
      clearAnonSession()
      set({ isLoading: false, isAuthenticated: false, anonSession: null, user: null, org: null, usage: null })
    } else {
      // Load anonymous session if exists
      const anonSession = getAnonSession()
      set({ isLoading: false, isAuthenticated: false, anonSession, user: null, org: null, usage: null })
    }
  },

  acceptPendingInviteIfNeeded: async () => {
    const pendingInvite = localStorage.getItem('pending_invite')
    if (!pendingInvite) return false

    try {
      const { token, inviteId, orgId } = JSON.parse(pendingInvite)
      if (!token || !inviteId || !orgId) {
        localStorage.removeItem('pending_invite')
        return false
      }

      await teamApi.acceptInvite(token, inviteId, orgId)
      localStorage.removeItem('pending_invite')
      await get().loadUser()
      return true
    } catch (error) {
      console.log('Pending invite acceptance failed')
      localStorage.removeItem('pending_invite')
      return false
    }
  },

  // --- Load user data ---
  loadUser: async () => {
    try {
      const tokenUser = getUserFromToken()
      if (!tokenUser) {
        set({ isLoading: false, isAuthenticated: false, user: null, org: null, usage: null })
        return
      }

      set({ user: tokenUser, isAuthenticated: true, org: null, usage: null })

      // Load org data
      try {
        const orgData = await authApi.getOrg()
        const trustedPlatformAdmin = hasTrustedPlatformAdminState(orgData.org) ||
          hasTrustedPlatformAdminState(orgData.usage) ||
          hasTrustedPlatformAdminState(orgData)
        const effectivePlan = getEffectivePlan({
          user: { ...tokenUser, isPlatformAdmin: trustedPlatformAdmin },
          org: orgData.org,
          usage: orgData.usage,
        })

        set({
          org: orgData.org,
          usage: orgData.usage || null,
          user: {
            ...tokenUser,
            orgId: orgData.org?.orgId || null,
            role: orgData.org?.ownerId === tokenUser.id ? 'owner' : tokenUser.role,
            plan: effectivePlan,
            isPlatformAdmin: trustedPlatformAdmin,
            manualPlanOverride: Boolean(
              orgData.org?.manualPlanOverride ||
              orgData.org?.billingSource === 'manual_override' ||
              orgData.org?.billing?.manualPlanOverride ||
              orgData.org?.billing?.billingSource === 'manual_override' ||
              orgData.usage?.manualPlanOverride ||
              orgData.usage?.billingSource === 'manual_override' ||
              orgData.usage?.billing?.manualPlanOverride ||
              orgData.usage?.billing?.billingSource === 'manual_override'
            ),
            manualPlanOverridePlan:
              orgData.org?.manualPlanOverridePlan ||
              orgData.org?.billingSource === 'manual_override' && (orgData.org?.plan || orgData.org?.subscriptionPlan) ||
              orgData.org?.billing?.manualPlanOverridePlan ||
              orgData.usage?.manualPlanOverridePlan ||
              orgData.usage?.billingSource === 'manual_override' && (orgData.usage?.plan || orgData.usage?.subscriptionPlan) ||
              orgData.usage?.billing?.manualPlanOverridePlan ||
              '',
          }
        })
      } catch {
        // User may not have an org yet - that's ok
        set({ org: null, usage: null })
      }

      set({ isLoading: false })

    } catch {
      set({ isLoading: false, isAuthenticated: false, user: null, org: null, usage: null })
    }
  },

  // --- Refresh org data ---
  refreshOrg: async () => {
    try {
      const orgData = await authApi.getOrg()
      set({ org: orgData.org })
    } catch {
      set({ org: null })
    }
  },

  // --- Refresh usage ---
  refreshUsage: async () => {
    try {
      const data = await authApi.getUsage()
      set({ usage: data })
    } catch {
      set({ usage: null })
    }
  },

  // --- Create org on first signup ---
  createOrg: async (companyName, email, name) => {
    try {
      const data = await authApi.createOrg({ companyName, email, name })
      await get().loadUser()
      return { success: true, orgId: data.orgId }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  // --- Anonymous session ---
  startAnonSession: async (jobTitle, jobDescription) => {
    try {
      const data = await anonApi.createSession(jobTitle, jobDescription)
      const session = {
        sessionId: data.sessionId,
        accessToken: data.accessToken,
        claimToken: data.claimToken,
        jobId: data.jobId,
        jobTitle,
        jobDescription,
        maxCVs: data.maxCVs,
        expiresAt: data.expiresAt,
      }
      setAnonSession(session)
      set({ anonSession: session })
      return { success: true, session }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  claimAnonSession: async () => {
    const { anonSession } = get()
    if (!anonSession?.sessionId) return

    try {
      const result = await anonApi.claimSession(anonSession)
      if (result?.success === false) return

      clearAnonSession()
      set({ anonSession: null })
    } catch {
      // silent - session may have expired
    }
  },

  clearAnonSession: () => {
    clearAnonSession()
    set({ anonSession: null })
  },

  // --- Auth actions ---
  login: () => redirectToLogin(),
  signUp: () => redirectToSignUp(),
  logout: () => {
    clearTokens()
    clearAnonSession()
    authLogout()
  },

  // --- Update local user state ---
  setUser: (user) => set({ user }),
  setOrg: (org) => set({ org }),
}))

export default useAuthStore
